import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { compositeOnWhite } from "@/lib/tool-kit/diagrams/color";
import {
  REGION_SETS,
  type TextBlock,
  type VennLayout,
} from "@/lib/tool-kit/diagrams/venn";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A Venn diagram from `layoutVenn`.
 *
 * Circles are filled with their set's colour at a quarter strength, so an overlap is
 * visibly darker than either set alone, and outlined at full strength on top of every
 * fill — an outline is never hidden under a neighbour's fill. Text inside a region takes
 * dark or white ink by the colour actually under it.
 */

/** Strength of each circle's fill. */
export const VENN_FILL_OPACITY = 0.25;

function Block({ block, fill }: { block: TextBlock; fill: string }) {
  return (
    <TextLines
      bold={block.bold}
      fill={fill}
      fontSize={block.fontSize}
      lineHeight={block.lineHeight}
      lines={block.lines}
      top={block.cy - block.height / 2}
      tops={block.tops}
      x={block.cx}
    />
  );
}

export function VennDiagram({
  id,
  layout,
  colors,
  label,
  description,
}: {
  id: string;
  layout: VennLayout;
  /** One colour per set, A first. */
  colors: readonly string[];
  label: string;
  description: string;
}) {
  const { universe } = layout;
  return (
    <ChartSvg
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {universe ? (
        <g>
          <rect
            fill="none"
            height={px(universe.rect.height)}
            stroke={CHART_INK.text}
            strokeWidth={1.25}
            width={px(universe.rect.width)}
            x={px(universe.rect.x)}
            y={px(universe.rect.y)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={13}
            fontWeight="bold"
            x={px(universe.labelX)}
            y={px(universe.labelY)}
          >
            {universe.label}
          </text>
        </g>
      ) : null}
      {layout.circles.map((circle, index) => (
        <circle
          cx={px(circle.cx)}
          cy={px(circle.cy)}
          fill={colors[index]}
          fillOpacity={VENN_FILL_OPACITY}
          key={`fill-${colors[index]}-${circle.cx}-${circle.cy}`}
          r={px(circle.r)}
        />
      ))}
      {layout.circles.map((circle, index) => (
        <circle
          cx={px(circle.cx)}
          cy={px(circle.cy)}
          fill="none"
          key={`line-${colors[index]}-${circle.cx}-${circle.cy}`}
          r={px(circle.r)}
          stroke={colors[index]}
          strokeWidth={2}
        />
      ))}
      {layout.names.map((name) => (
        <TextLines
          bold
          fontSize={name.fontSize}
          key={`name-${name.x}-${name.y}`}
          lineHeight={name.lineHeight}
          lines={name.lines}
          top={name.y}
          x={name.x}
        />
      ))}
      {layout.regions.map(({ id: region, block }) =>
        block ? (
          <Block
            block={block}
            fill={textColorOn(
              compositeOnWhite(
                REGION_SETS[region].map((set) => colors[set] ?? CHART_INK.axis),
                VENN_FILL_OPACITY
              )
            )}
            key={region}
          />
        ) : null
      )}
      {universe?.outside ? (
        <Block block={universe.outside} fill={CHART_INK.text} />
      ) : null}
    </ChartSvg>
  );
}
